import { useEffect, useMemo, useState } from 'react';
import { getPlayerAvatar } from '../../utils/player';
import { getCriterion } from '../../utils/eventCriteria';
import {
  describeCriterion,
  getProgressFraction,
  getProgressHint,
} from '../../utils/eventsView';
import EventLeaderboard from './EventLeaderboard';

const EVENT_TABS = [
  { key: 'active', label: 'ACTIVE' },
  { key: 'past', label: 'PAST' },
];

const playerCountText = (event, standings) => {
  const n = Array.isArray(event.participants) ? event.participants.length : standings.length;
  return `${n} ${n === 1 ? 'player' : 'players'}`;
};

// Event card from Figma (791:17868 active, 791:17894 past): trophy badge or the
// winner's avatar, name and criterion, a progress bar, then the hint and player count.
function EventCard({ event, summary, allPlayers, index, onOpen }) {
  const standings = summary?.standings || [];
  const isEnded = event.status === 'ended';
  const winnerNames = isEnded ? summary?.winner?.players || [] : [];
  const progress = isEnded ? null : getProgressFraction(event);

  return (
    <button
      type="button"
      className="event-card play-build-item"
      style={{ '--pi': Math.min(index, 8) }}
      onClick={() => onOpen(event.id)}
      aria-label={`Open ${event.name}`}
    >
      <div className="event-card__top">
        {winnerNames.length > 0 ? (
          <img
            src={getPlayerAvatar(allPlayers, winnerNames[0])}
            alt=""
            className="avatar event-card__avatar"
          />
        ) : (
          <span className="event-card__badge" aria-hidden="true">
            <span className="material-symbols-rounded">trophy</span>
          </span>
        )}
        <div className="event-card__heading">
          <h3 className="event-card__name">{event.name}</h3>
          <p className="event-card__criterion">{describeCriterion(event)}</p>
        </div>
      </div>

      <div className={`event-progress ${progress === null ? 'event-progress--hidden' : ''}`} aria-hidden="true">
        {progress !== null && (
          <div className="event-progress__bar" style={{ width: `${Math.round(progress * 100)}%` }} />
        )}
      </div>

      <div className="event-card__footer">
        {winnerNames.length > 0 ? (
          <span className="event-card__winner">
            {winnerNames.length > 1 ? 'Winners' : 'Winner'}: {winnerNames.join(' & ')}
          </span>
        ) : (
          <span className="event-card__hint">{getProgressHint(event, summary?.matchesPlayed || 0)}</span>
        )}
        <span className="event-card__players">
          <span className="material-symbols-rounded" aria-hidden="true">group</span>
          {playerCountText(event, standings)}
        </span>
      </div>
    </button>
  );
}

// Champion card (Figma 791:18371), keeping the player card motion: the avatar pops in
// with a gold ring pulse, then a gold shine sweeps across.
function EventChampion({ names, standings, event, allPlayers }) {
  const top = standings.find((row) => row.rank === 1);
  const crit = getCriterion(event.criterion.stat);
  const isTie = names.length > 1;

  return (
    <div className="event-champion pc-anim-card">
      <span className="event-champion__kicker pc-anim-name">
        <span className="material-symbols-rounded" aria-hidden="true">trophy</span>
        {isTie ? 'Joint champions' : 'Champion'}
      </span>
      <div className="event-champion__avatars">
        {names.slice(0, 3).map((name) => (
          <span key={name} className="pc-anim-avatar pc-anim-avatar-ring">
            <img src={getPlayerAvatar(allPlayers, name)} alt="" className="event-champion__avatar" />
          </span>
        ))}
      </div>
      <h3 className="event-champion__name pc-anim-name">{names.join(' & ')}</h3>
      {top && (
        <p className="event-champion__value pc-anim-actions">
          {top.formattedCriterion} {crit.label.toLowerCase()}
        </p>
      )}
    </div>
  );
}

// Event page (Figma 788:17017 in progress, 791:18092 no matches, 791:18371 ended):
// back / name / settings, the champion once ended, the standings, then the event details.
function EventDetail({ event, summary, allPlayers, canManage, isSessionActive, onBack, onEdit }) {
  const standings = summary?.standings || [];
  const isEnded = event.status === 'ended';
  const champions = isEnded ? summary?.winner?.players || [] : [];
  const progress = getProgressFraction(event);
  const tieBreakers = (event.tieBreakers || []).map((tb) => getCriterion(tb.stat).label.toLowerCase());
  const canEdit = canManage && !isSessionActive;

  return (
    <div className="event-page play-build-item">
      <div className="event-page__header">
        <button type="button" className="event-page__icon-btn" onClick={onBack} aria-label="Back to events">
          <span className="material-symbols-rounded" aria-hidden="true">arrow_back</span>
        </button>
        <h2 className="event-page__title">{event.name}</h2>
        {canEdit ? (
          <button type="button" className="event-page__icon-btn" onClick={() => onEdit(event)} aria-label="Event settings">
            <span className="material-symbols-rounded" aria-hidden="true">settings</span>
          </button>
        ) : (
          <span className="event-page__icon-btn" aria-hidden="true" />
        )}
      </div>

      {champions.length > 0 && (
        <EventChampion names={champions} standings={standings} event={event} allPlayers={allPlayers} />
      )}

      {standings.length > 0 ? (
        <EventLeaderboard event={event} standings={standings} allPlayers={allPlayers} champions={champions} />
      ) : (
        <p className="event-page__empty">No matches played</p>
      )}

      <div className="event-details">
        <h3 className="event-details__title">Event details</h3>
        <ul className="event-details__list">
          <li>{describeCriterion(event)}</li>
          {tieBreakers.length > 0 && <li>Ties broken by {tieBreakers.join(', then ')}</li>}
          {event.minMatches > 0 && (
            <li>At least {event.minMatches} {event.minMatches === 1 ? 'match' : 'matches'} to qualify</li>
          )}
        </ul>
        <div className={`event-progress event-details__progress ${progress === null ? 'event-progress--hidden' : ''}`} aria-hidden="true">
          {progress !== null && (
            <div className="event-progress__bar" style={{ width: `${Math.round(progress * 100)}%` }} />
          )}
        </div>
        <div className="event-card__footer">
          <span className="event-card__hint">{getProgressHint(event, summary?.matchesPlayed || 0)}</span>
          <span className="event-card__players">
            <span className="material-symbols-rounded" aria-hidden="true">group</span>
            {playerCountText(event, standings)}
          </span>
        </div>
      </div>
    </div>
  );
}

// Events tab (#50): Active and Past events, each opening a detail page with its leaderboard.
// Owner and admins create events here and edit, end or delete them from the settings
// cog on an event; members only view them.
export default function EventsView({
  events = [],
  eventSummaries = {},
  allPlayers = [],
  canManage = false,
  isSessionActive = false,
  isLoading = false,
  focusSection = null,
  onFocusHandled,
  onCreate,
  onEdit,
}) {
  const [selectedId, setSelectedId] = useState(null);
  const [tab, setTab] = useState(focusSection === 'past' ? 'past' : 'active');

  const { active, past } = useMemo(() => ({
    active: events.filter((e) => e.status !== 'ended'),
    past: events.filter((e) => e.status === 'ended'),
  }), [events]);

  const selected = selectedId ? events.find((e) => e.id === selectedId) : null;

  // Hall of Fame "Most Event Wins" opens the Past tab
  useEffect(() => {
    if (focusSection !== 'past') return;
    setSelectedId(null);
    setTab('past');
    if (onFocusHandled) onFocusHandled();
  }, [focusSection, onFocusHandled]);

  // Scroll to the top when switching between list and detail
  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [selectedId]);

  if (selected) {
    return (
      <EventDetail
        event={selected}
        summary={eventSummaries[selected.id]}
        allPlayers={allPlayers}
        canManage={canManage}
        isSessionActive={isSessionActive}
        onBack={() => setSelectedId(null)}
        onEdit={onEdit}
      />
    );
  }

  if (isLoading) {
    return (
      <div className="events-empty">
        <p className="events-empty__text">Loading events…</p>
      </div>
    );
  }

  const shown = tab === 'past' ? past : active;
  let emptyText = 'Finished events and their champions land here.';
  if (tab === 'active') {
    if (events.length > 0) emptyText = 'No events running right now.';
    else if (canManage) emptyText = 'Run a league, a Real MVP week or a Deuce master month. Backdated events fill in from match history straight away.';
    else emptyText = 'When an owner or admin creates an event, its leaderboard shows up here.';
  }

  // Figma 791:17767 (Active) and 791:17914 (Past): one card with ACTIVE / PAST tabs
  return (
    <div className="events-card play-build-item">
      <div className="tabs-container events-tabs" role="tablist" aria-label="Events">
        <div className="tabs-group">
          {EVENT_TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              role="tab"
              id={`events-tab-${t.key}`}
              aria-selected={tab === t.key}
              aria-controls="events-tabpanel"
              className={`tab ${tab === t.key ? 'tab-selected' : 'tab-unselected'}`}
              onClick={() => setTab(t.key)}
            >
              <span className="tab-contents">
                <span className="tab-label">{t.label}</span>
              </span>
            </button>
          ))}
        </div>
      </div>

      <div
        key={tab}
        id="events-tabpanel"
        role="tabpanel"
        aria-labelledby={`events-tab-${tab}`}
        className="events-card__content"
      >
        {shown.length > 0 ? (
          <div className="events-list">
            {shown.map((event, i) => (
              <EventCard
                key={event.id}
                event={event}
                summary={eventSummaries[event.id]}
                allPlayers={allPlayers}
                index={i}
                onOpen={setSelectedId}
              />
            ))}
          </div>
        ) : (
          <p className="events-card__empty">{emptyText}</p>
        )}

        {tab === 'active' && canManage && !isSessionActive && (
          <button type="button" className="btn btn-primary btn-full events-card__create" onClick={onCreate}>
            <span className="btn-text">CREATE NEW EVENT</span>
          </button>
        )}
      </div>
    </div>
  );
}
